import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  GetUserCommand,
  AssociateSoftwareTokenCommand,
  VerifySoftwareTokenCommand,
  SetUserMFAPreferenceCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { REFRESH_TOKEN_KEY, ACCESS_TOKEN_KEY, ID_TOKEN_KEY } from './constants';
import { isTokenInvalid, removeTokens } from './helpers';
import store from './store';
import {
  setAmazonDataImportStatus,
  setHasNewAmazonCredentials,
} from './store/amazon/amazonSlice';
import { clearUserState } from './store/user/userSlice';
export const Region = import.meta.env.VITE_APP_REGION;
export const ClientId = import.meta.env.VITE_APP_USERPOOL_CLIENT_ID;
export const client = new CognitoIdentityProviderClient({ region: Region });

interface ForgotPasswordInput {
  email: string;
}

interface ConfirmSignUpInput extends ForgotPasswordInput {
  confirmationCode: string;
}
interface ConfirmForgotPasswordInput extends ConfirmSignUpInput {
  password: string;
}

const updateToken = async () => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY) || '';

  if (!accessToken) {
    return;
  }

  if (isTokenInvalid(accessToken)) {
    await refreshTokens();
  }
};

export const refreshAccessToken = async (refreshToken: string) =>
  await client.send(
    new InitiateAuthCommand({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
      ClientId,
    })
  );

export const confirmSignUp = async ({
  email,
  confirmationCode,
}: ConfirmSignUpInput) =>
  await client.send(
    new ConfirmSignUpCommand({
      ClientId,
      ConfirmationCode: confirmationCode,
      Username: email,
    })
  );

export const forgotPassword = async ({ email }: ForgotPasswordInput) =>
  await client.send(
    new ForgotPasswordCommand({
      ClientId,
      Username: email,
    })
  );

export const confirmForgotPassword = async ({
  email,
  confirmationCode,
  password,
}: ConfirmForgotPasswordInput) =>
  await client.send(
    new ConfirmForgotPasswordCommand({
      ClientId,
      Username: email,
      ConfirmationCode: confirmationCode,
      Password: password,
    })
  );

export const refreshTokens = async () => {
  try {
    const res = await refreshAccessToken(
      localStorage.getItem(REFRESH_TOKEN_KEY) || ''
    );
    localStorage.setItem(
      ACCESS_TOKEN_KEY,
      res.AuthenticationResult!.AccessToken!
    );
    localStorage.setItem(ID_TOKEN_KEY, res.AuthenticationResult!.IdToken!);
  } catch (error) {
    sessionStorage.clear();
    store.dispatch(clearUserState());
    store.dispatch(setAmazonDataImportStatus(undefined));
    store.dispatch(setHasNewAmazonCredentials(false));
    removeTokens();
  }
};

export const getCognitoUser = async () => {
  await updateToken();
  const user = await client.send(
    new GetUserCommand({
      AccessToken: localStorage.getItem(ACCESS_TOKEN_KEY)!,
    })
  );

  return user;
};

export const associateSoftwareToken = async () => {
  await updateToken();
  const res = await client.send(
    new AssociateSoftwareTokenCommand({
      AccessToken: localStorage.getItem(ACCESS_TOKEN_KEY)!,
    })
  );
  return res;
};

export const verifySoftwareToken = async (userCode: string) => {
  await updateToken();
  const res = await client.send(
    new VerifySoftwareTokenCommand({
      AccessToken: localStorage.getItem(ACCESS_TOKEN_KEY)!,
      UserCode: userCode,
    })
  );
  return res;
};

export const setSoftwareTokenMfaSettings = async (
  Enabled: boolean,
  PreferredMfa: boolean
) => {
  await updateToken();
  const res = await client.send(
    new SetUserMFAPreferenceCommand({
      AccessToken: localStorage.getItem(ACCESS_TOKEN_KEY)!,
      SoftwareTokenMfaSettings: {
        Enabled,
        PreferredMfa,
      },
    })
  );
  return res;
};
